import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Close as CloseIcon, Lock as LockIcon, ArrowBack } from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import { useProjectWorkflow } from '../contexts/ProjectWorkflowContext';
import { useAuth } from '../contexts/AuthContext';
import { cartService } from '../services/cartService';
import { clientPlanService } from '../services/clientPlanService';
import CheckoutModal from '../components/cart/CheckoutModal';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ProjectCartPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { selectedProject, goToStep, markStepDone } = useProjectWorkflow();

  // After a successful checkout the placements change, so the Explore Sites
  // usage data ("Already Ordered" badge, Usage Nx) is stale. Invalidate the
  // cached queries so going back to Explore Sites shows fresh labels without
  // a manual page refresh.
  const invalidateSiteUsageCaches = () => {
    void queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
    void queryClient.invalidateQueries({ queryKey: ['ordered-sites'] });
  };
  const { isClient, isAgency } = useAuth();
  const usesPaymentCheckout = isClient || isAgency;
  const projectId = searchParams.get('projectId') || selectedProject?.id || '';
  const planId = searchParams.get('planId') || '';

  // Once a plan's checkout completes, the plan/order is fulfilled and should no
  // longer appear in Planning. Best-effort: never block the checkout flow on it.
  const fulfillSavedOrder = async () => {
    if (!planId) return;
    try {
      await clientPlanService.fulfillSavedOrder(planId);
      await queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
    } catch {
      // ignore - the order already completed; planning cleanup is non-critical
    }
  };
  const { cart, loading, removeFromCart, cartTotal, refreshCart } = useCart(projectId || undefined);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // True only after a COMPLETED checkout, so closing the modal advances to Order
  // Status on success but stays on this step when the user cancels.
  const checkoutSucceededRef = useRef(false);

  // Finalize the Stripe payment when the client returns from checkout. The
  // backend appends ?paymentStatus=success&paymentAttemptId=...&session_id=...
  // to this page's URL; without calling /payments/finalize the placements are
  // never created (so nothing shows up in Order Status).
  const paymentStatus = searchParams.get('paymentStatus') || '';
  const paymentAttemptId = searchParams.get('paymentAttemptId') || '';
  const stripeSessionId = searchParams.get('session_id') || '';
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const finalizedRef = useRef(false);

  useEffect(() => {
    if (paymentStatus !== 'success' || !paymentAttemptId || !stripeSessionId || finalizedRef.current) {
      return;
    }
    finalizedRef.current = true;
    setFinalizing(true);
    (async () => {
      try {
        await cartService.finalizeStripeCheckout({ paymentAttemptId, sessionId: stripeSessionId });
        await refreshCart();
        await fulfillSavedOrder();
        invalidateSiteUsageCaches();
        markStepDone('cart');
        // Navigate directly to Order Status. goToStep depends on selectedProject
        // which may not be hydrated yet on a fresh return from Stripe.
        const clientId = searchParams.get('clientId') || selectedProject?.clientId || '';
        const q = new URLSearchParams();
        if (projectId) q.set('projectId', projectId);
        if (clientId) q.set('clientId', clientId);
        navigate(`/guest-blog-placements${q.toString() ? `?${q.toString()}` : ''}`, { replace: true });
      } catch (error: any) {
        setFinalizeError(
          error.response?.data?.error || 'Payment completed but finalization failed. Please contact support.'
        );
        // Drop the payment params so a refresh doesn't retry against a used session.
        const updated = new URLSearchParams(location.search);
        updated.delete('paymentStatus');
        updated.delete('paymentAttemptId');
        updated.delete('session_id');
        updated.delete('paymentCancelled');
        navigate(`${location.pathname}${updated.toString() ? `?${updated.toString()}` : ''}`, { replace: true });
      } finally {
        setFinalizing(false);
      }
    })();
  }, [paymentStatus, paymentAttemptId, stripeSessionId, refreshCart, markStepDone, navigate, location.pathname, location.search, searchParams, selectedProject, projectId]);

  const items = cart?.cartItems || [];

  const isValidUrl = (value?: string | null) => {
    const v = (value || '').trim();
    if (!v) return false;
    try {
      const parsed = new URL(v);
      return /^https?:$/i.test(parsed.protocol);
    } catch {
      return false;
    }
  };

  const docUrlOf = (item: { contentUrl?: string | null; contentDocUrl?: string | null }) =>
    item.contentUrl || item.contentDocUrl || '';

  const missingDocCount = items.filter((it) => !isValidUrl(docUrlOf(it))).length;
  const allDocsProvided = items.length > 0 && missingDocCount === 0;

  const handlePlaceOrder = () => {
    if (items.length === 0) return;
    // Checkout requires a content doc link for every site (added in Place Order).
    if (!allDocsProvided) {
      goToStep('order');
      return;
    }
    setCheckoutOpen(true);
  };

  if (!projectId) {
    return <p style={{ color: 'var(--text-muted)' }}>Select a project first.</p>;
  }

  if (finalizing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: 48 }}>
        <LoadingSpinner />
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Finalizing your payment and creating placements…</p>
      </div>
    );
  }

  if (loading && !cart) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 13 }}>Cart &amp; checkout</div>
      {finalizeError && (
        <div
          style={{
            background: '#FEF2F2',
            border: '1px solid #EF4444',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 13,
            fontSize: 13,
            color: '#991B1B',
          }}
        >
          {finalizeError}
        </div>
      )}
      <div
        style={
          usesPaymentCheckout
            ? { display: 'flex', justifyContent: 'center', paddingTop: 24 }
            : { display: 'grid', gridTemplateColumns: '1fr 240px', gap: 12 }
        }
      >
        {/* Site review table — hidden for clients/agencies (already shown in Place Order) */}
        {!usesPaymentCheckout && (
        <div
          style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--border)',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 60px 80px 30px',
              padding: '9px 16px',
              background: 'var(--surface2)',
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
            }}
          >
            <span>Site</span>
            <span>DA</span>
            <span>Price</span>
            <span />
          </div>
          {items.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
              Your cart is empty.
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.5fr 60px 80px 30px',
                  padding: '11px 16px',
                  borderBottom: '0.5px solid var(--border)',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 500 }}>{item.siteUrl}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{item.siteDa}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>${item.price}</span>
                <button
                  type="button"
                  onClick={() => void removeFromCart(item.id)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
            ))
          )}
        </div>
        )}
        <div
          style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--border)',
            borderRadius: 10,
            padding: usesPaymentCheckout ? 22 : 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
            height: 'fit-content',
            width: usesPaymentCheckout ? 340 : undefined,
            maxWidth: '100%',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 500 }}>Order total</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--accent)' }}>${cartTotal}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {items.length} site{items.length !== 1 ? 's' : ''} · incl. content writing &amp; placement
          </div>
          {items.length > 0 && !allDocsProvided && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--red, #dc2626)',
                background: 'rgba(220,38,38,0.06)',
                border: '0.5px solid rgba(220,38,38,0.3)',
                borderRadius: 7,
                padding: '7px 9px',
              }}
            >
              {missingDocCount} site{missingDocCount !== 1 ? 's' : ''} missing a content doc link. Add{' '}
              {missingDocCount !== 1 ? 'them' : 'it'} in Place Order before checkout.
            </div>
          )}
          <button
            type="button"
            className="wf-btn-create"
            style={{ justifyContent: 'center', marginTop: 4 }}
            disabled={items.length === 0}
            title={allDocsProvided ? undefined : 'Add content doc links in Place Order first'}
            onClick={handlePlaceOrder}
          >
            <LockIcon sx={{ fontSize: 14 }} />
            {allDocsProvided ? 'Place order & pay' : 'Add doc links to continue'}
          </button>
          <button
            type="button"
            onClick={() => goToStep('order')}
            style={{
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              padding: '8px',
              borderRadius: 7,
              border: '0.5px solid var(--border)',
              background: 'var(--surface2)',
              cursor: 'pointer',
            }}
          >
            <ArrowBack sx={{ fontSize: 13 }} />
            Back to order
          </button>
          {usesPaymentCheckout && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                fontSize: 11,
                color: 'var(--text-muted)',
                marginTop: 2,
              }}
            >
              <LockIcon sx={{ fontSize: 13 }} />
              Secure payment 
            </div>
          )}
        </div>
      </div>

      {checkoutOpen && (
        <CheckoutModal
          open={checkoutOpen}
          onCheckoutSuccess={() => {
            // Admin direct checkout completed. Mark success so the subsequent
            // onClose advances to Order Status (after the success message).
            // Clients complete via Stripe redirect, handled by the paymentStatus
            // effect above — not this callback.
            checkoutSucceededRef.current = true;
            void fulfillSavedOrder();
            invalidateSiteUsageCaches();
            markStepDone('cart');
          }}
          onClose={() => {
            setCheckoutOpen(false);
            if (checkoutSucceededRef.current) {
              checkoutSucceededRef.current = false;
              goToStep('status');
            }
            // Otherwise it was a cancel/dismiss — stay on the Cart & Checkout step.
          }}
          projectId={projectId}
          projectName={cart?.projectName || selectedProject?.projectName || 'Project'}
          clientName={cart?.clientName || selectedProject?.clientName || 'Client'}
          planId={planId || undefined}
        />
      )}
    </div>
  );
};

export default ProjectCartPage;
